import {useEffect, useState} from 'react'
import type {Elements, IContentItem} from '@kontent-ai/delivery-sdk'
import {clsx} from 'clsx'
import type {MikmakButtonContentItem} from '@/products/where-to-buy/mikmak/mikmak-button'
import {MikMakButtonBlock} from '@/products/where-to-buy/mikmak/mikmak-button'
import type {Block, TersedAsset} from '@/services/kontent-ai/types'
import {RichTextRenderer} from '@/products/flexible-web-toolkit/components/rich-text-renderer'
import type {MikmakInPageContentItem} from '@/products/where-to-buy/mikmak/mikmak-in-page'
import {MikMakInPageBlock} from '@/products/where-to-buy/mikmak/mikmak-in-page'
import {useWindowSize} from '@/utilities/hooks/use-window-size'
import {Header} from '@/products/flexible-web-toolkit/components/block-header'
import {PackshotImage} from '@/products/flexible-web-toolkit/components/block-header/packshot-image'
import {HeaderImage} from '@/products/flexible-web-toolkit/components/block-header/image'
import type {ClickToBuyMultiProductContentItem} from '@/products/where-to-buy/click2cart/click-to-buy-multi-product/click-to-buy-multi-product'
import {ClickToBuyMultiProductBlock} from '@/products/where-to-buy/click2cart/click-to-buy-multi-product'
import {ClickToCartBlock} from '@/products/where-to-buy/smartcommerce'
import type {ClickToCartContentItem} from '@/products/where-to-buy/smartcommerce'
import {ClickToBuySingleProductBlock} from '@/products/where-to-buy/click2cart/click-to-buy-single-product'
import type {ClickToBuySingleProductContentItem} from '@/products/where-to-buy/click2cart/click-to-buy-single-product/click-to-buy-single-product'
import {tailwindColors} from '@/products/flexible-web-toolkit/colors/registry'
import {getBackgroundTheme} from '@/products/flexible-web-toolkit/colors'
import {colorWithFallback} from '@/utilities/hooks/color-with-fallback'
import {spacing} from '../../styles'
import type {CtaButtonContentItem} from '../cta-button'
import {ReusableCTAButtonComponent} from '../cta-button'
import {LinkBlock, type LinkContentItem} from '../link'

export type SectionHeaderBlockContentItem = IContentItem<{
	title: Elements.TextElement
	content: Elements.RichTextElement
	logo: Elements.AssetsElement
	image: Elements.AssetsElement
	mobileImage: Elements.AssetsElement
	ctaButton: Elements.LinkedItemsElement<CtaButtonContentItem>
	clickToBuySingleProduct: Elements.LinkedItemsElement<ClickToBuySingleProductContentItem>
	clickToCart: Elements.LinkedItemsElement<ClickToCartContentItem>
	multiProductClickToBuy: Elements.LinkedItemsElement<ClickToBuyMultiProductContentItem>
	mikmakButton: Elements.LinkedItemsElement<MikmakButtonContentItem>
	mikmakInPage: Elements.LinkedItemsElement<MikmakInPageContentItem>
	mikmakInPageAlignment: Elements.MultipleChoiceElement
	mikmakInPageTopPadding: Elements.NumberElement
	textLink: Elements.LinkedItemsElement<LinkContentItem>
	fullBackgroundImage: Elements.MultipleChoiceElement
	hasWhiteText: Elements.MultipleChoiceElement
	hasWhiteBackground: Elements.MultipleChoiceElement
	mediaAlignRight: Elements.MultipleChoiceElement
	mediaLeftPosition: Elements.TextElement
	mediaTopPosition: Elements.TextElement
	packshotImage: Elements.AssetsElement
	packshotAlign: Elements.MultipleChoiceElement
	packshotRightPosition: Elements.TextElement
	packshotTopPosition: Elements.TextElement
	snippetSpacingSpacing: Elements.MultipleChoiceElement
	snippetLayoutBackgroundColorColor: Elements.MultipleChoiceElement
	/** New color picker from snippet_layout_background_color snippet */
	snippetLayoutBackgroundColorBrandColor: Elements.CustomElement
}>

export const SectionHeaderBlock: Block<SectionHeaderBlockContentItem> = ({
	block,
	page,
	...context
}) => {
	const pageConfig = page.elements.config[0]
	const hasFullBackground =
		block.elements.fullBackgroundImage[0]?.codename === 'yes'
	const componentSpacing =
		// eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Necessary for type safety - codename isn't actually always of type spacing
		spacing[
			block.elements.snippetSpacingSpacing[0]
				?.codename as keyof typeof spacing
		] || spacing.none

	// Priority: new color picker > legacy dropdown > page config > fallback
	const {colorId: brand} = colorWithFallback({
		pageConfig,
	})

	const legacySnippetColor =
		block.elements.snippetLayoutBackgroundColorColor[0]?.codename || 'none'

	// When the new color picker has a value, it takes precedence over the legacy dropdown
	const hasNewPickerColor = Boolean(
		block.elements.snippetLayoutBackgroundColorBrandColor
	)
	const snippetColor = hasNewPickerColor ? 'none' : legacySnippetColor

	const liteBackground =
		legacySnippetColor === 'brand' && !hasNewPickerColor
			? ` !bg-${brand}-light bg-default-light-background`
			: ''

	const mediaAlignRight =
		block.elements.mediaAlignRight[0]?.codename === 'yes'

	const mikmakInPageAlign =
		block.elements.mikmakInPageAlignment[0]?.codename || 'left'
	const mikmakInPageTopPadding = Math.min(
		Math.max(block.elements.mikmakInPageTopPadding || 0, 0),
		380
	)
	/*
	 *	if the snippetColor doesn't equal none then convert the value from snake case to hyphen case
	 *	and also prepend it with bg- to match the tailwind classes e.g. from pet_green to bg-pet-green
	 */

	const availableColors = Object.keys(tailwindColors)
	const colorToUse = availableColors.includes(snippetColor)
		? snippetColor.replace(
				/(?<lowercase>[a-z])(?<uppercase>[A-Z])/g,
				'$1-$2'
			)
		: snippetColor
				.replace(/(?<lowercase>[a-z])(?<uppercase>[A-Z])/g, '$1-$2')
				.replace(/[\s_]+/g, '-')

	// When new picker is active or no block-level color is set, use backgroundTheme
	// (covers both block-level picker and page config colors); otherwise legacy logic
	// When new picker is active, use its theme; otherwise fall back to legacy logic

	const {colorId: snippetColorId} = colorWithFallback({
		blockColor: block.elements.snippetLayoutBackgroundColorBrandColor,
	})

	const bgSnippetColor = (() => {
		if (hasNewPickerColor) {
			return getBackgroundTheme(snippetColorId || 'bg-white')
		}
		return snippetColor === 'none' ? 'bg-white' : `bg-${colorToUse}`
	})()

	const [headerImage, setHeaderImage] = useState<TersedAsset | null>(null)

	const size = useWindowSize()

	useEffect(() => {
		if (
			hasFullBackground &&
			size.isMobile &&
			block.elements.mobileImage[0]
		) {
			setHeaderImage(block.elements.mobileImage[0] || null)
		} else if (
			size.isMobile &&
			!block.elements.mobileImage.length &&
			block.elements.packshotImage.length
		) {
			setHeaderImage(null)
		} else {
			setHeaderImage(block.elements.image[0] || null)
		}
	}, [
		block.elements.image,
		block.elements.mobileImage,
		block.elements.packshotImage.length,
		hasFullBackground,
		size,
	])

	const btnVariant = block.elements.ctaButton[0]?.elements.variant

	let backgroundBrandColor = 'bg-primary'
	if (brand) {
		backgroundBrandColor = `bg-${brand}`
	} else if (block.elements.ctaButton[0]?.elements.brandColor) {
		backgroundBrandColor = `bg-${block.elements.ctaButton[0]?.elements.brandColor}`
	} else if (btnVariant?.[0]?.codename) {
		backgroundBrandColor = btnVariant[0].codename
	}

	const hasWhiteInfoContainerBg =
		block.elements.hasWhiteBackground[0]?.codename === 'yes'
	const showBuyProductsClassname =
		block.elements.clickToBuySingleProduct[0] ||
		block.elements.multiProductClickToBuy[0] ||
		block.elements.clickToCart.length > 0
			? 'flex flex-wrap'
			: ''

	const mikmakInPageWidget = block.elements.mikmakInPage[0] ? (
		<div data-kontent-item-id={block.elements.mikmakInPage[0].system.id}>
			<MikMakInPageBlock
				block={block.elements.mikmakInPage[0]}
				page={page}
				{...context}
			/>
		</div>
	) : null

	return (
		<Header
			bgColor={bgSnippetColor}
			className={clsx(componentSpacing, liteBackground)}
			contain={!hasFullBackground}
			containerSize={hasFullBackground ? 'lg' : ''}
			fixedInfoWidth={!headerImage}
			fullBackgroundImage={hasFullBackground}
			hasWhiteInfoContainerBg={hasWhiteInfoContainerBg}
			infoSectionTopStack={mikmakInPageAlign === 'left'}
			logoImgAlt={block.elements.logo[0]?.description}
			logoImgSrc={block.elements.logo[0]?.url}
			noTheme
			reversedMobile
			widget={mikmakInPageAlign === 'right' ? mikmakInPageWidget : null}
		>
			{block.elements.title ? (
				<h1
					className={clsx(
						block.elements.hasWhiteText[0]?.codename === 'yes'
							? 'text-white'
							: '',
						'break-words'
					)}
					data-kontent-element-codename="title"
				>
					{block.elements.title}
				</h1>
			) : null}
			{block.elements.content.value !== '<p><br></p>' && (
				<RichTextRenderer
					className={clsx(
						block.elements.hasWhiteText[0]?.codename === 'yes'
							? 'text-white'
							: null,
						'break-words pb-2 pt-6 *:*:!mb-0'
					)}
					data-kontent-element-codename="content"
					element={block.elements.content}
					page={page}
					{...context}
				/>
			)}
			{block.elements.ctaButton[0] ? (
				<div
					className="flex flex-wrap "
					data-kontent-element-codename="cta_button"
				>
					{block.elements.ctaButton.map((button, i) => (
						<div
							className={clsx(
								'flex',
								block.elements.ctaButton.length >= 2
									? 'w-1/2'
									: 'w-fit'
							)}
							key={button.system.id}
						>
							<div
								className={clsx(
									'mt-6 flex flex-1 flex-col justify-center',
									i === 0 ? 'mr-1' : 'ml-1'
								)}
								data-kontent-item-id={button.system.id}
							>
								<ReusableCTAButtonComponent
									block={button}
									className={clsx(
										'mb-2 h-full text-white',
										backgroundBrandColor
									)}
									page={page}
									variant={brand ?? undefined}
									{...context}
								/>
							</div>
						</div>
					))}
				</div>
			) : null}
			<div className={showBuyProductsClassname}>
				{block.elements.clickToBuySingleProduct[0] ? (
					<div className="flex w-full flex-col">
						<div
							className="flex flex-1 justify-start"
							data-kontent-item-id={
								block.elements.clickToBuySingleProduct[0].system
									.id
							}
						>
							<ClickToBuySingleProductBlock
								block={
									block.elements.clickToBuySingleProduct[0]
								}
								page={page}
								{...context}
							/>
						</div>
					</div>
				) : null}
				{block.elements.multiProductClickToBuy[0] ? (
					<div className="flex w-full flex-col">
						<div
							className="flex flex-1 justify-start"
							data-kontent-item-id={
								block.elements.multiProductClickToBuy[0].system
									.id
							}
						>
							<ClickToBuyMultiProductBlock
								block={block.elements.multiProductClickToBuy[0]}
								page={page}
								{...context}
							/>
						</div>
					</div>
				) : null}
				{block.elements.clickToCart.map((widgetBlock, i) => (
					<div
						className="flex min-h-[294px] w-1/2"
						key={widgetBlock.elements.dataSourceId}
					>
						<div
							className={clsx(
								'mt-6 flex flex-1 flex-col justify-center',
								i === 0 ? 'mr-1' : 'ml-1'
							)}
							data-kontent-item-id={widgetBlock.system.id}
						>
							<ClickToCartBlock
								block={widgetBlock}
								page={page}
								{...context}
							/>
						</div>
					</div>
				))}
			</div>
			{/* eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- Necessary for now, TODO: add mikmak to all towers */}
			{block.elements.mikmakButton?.[0] ? (
				<div className="flex flex-wrap">
					<div className="flex w-full flex-col">
						<div
							className="flex flex-1 justify-start"
							data-kontent-item-id={
								block.elements.mikmakButton[0].system.id
							}
						>
							<MikMakButtonBlock
								block={block.elements.mikmakButton[0]}
								page={page}
								{...context}
							/>
						</div>
					</div>
				</div>
			) : null}
			{mikmakInPageAlign === 'left' ? (
				<div style={{paddingTop: mikmakInPageTopPadding}}>
					{mikmakInPageWidget}
				</div>
			) : null}
			{Boolean(block.elements.textLink[0]) && (
				<div className="mt-4 flex flex-wrap">
					{block.elements.textLink.map((link, i) => (
						<div className="flex w-1/2" key={link.system.id}>
							<div
								className={clsx(
									'flex flex-1 flex-col justify-center',
									i === 0 ? 'mr-1' : 'ml-1'
								)}
							>
								<LinkBlock
									block={link}
									className={`${
										block.elements.hasWhiteText[0]
											?.codename === 'yes'
											? 'text-white'
											: ''
									} text-btn cursor-pointer text-xs underline hover:no-underline focus:text-secondary focus:no-underline focus:outline-none`}
									page={page}
									{...context}
								/>
							</div>
						</div>
					))}
				</div>
			)}
			<HeaderImage
				alt={headerImage?.description}
				contain={!hasFullBackground}
				isMobile={size.isMobile}
				mediaAlignRight={mediaAlignRight}
				mediaLeftPosition={block.elements.mediaLeftPosition}
				mediaTopPosition={block.elements.mediaTopPosition}
				packshotImage={
					block.elements.packshotImage[0] ? (
						<PackshotImage
							alt={block.elements.packshotImage[0].description}
							hasMobileImage={
								block.elements.mobileImage.length > 0
							}
							mediaAlign={
								block.elements.packshotAlign[0]?.codename ===
								'yes'
							}
							mediaRightPosition={
								block.elements.packshotRightPosition
							}
							mediaTopPosition={
								block.elements.packshotTopPosition
							}
							src={block.elements.packshotImage[0].url}
						/>
					) : null
				}
				src={headerImage?.url}
			/>
		</Header>
	)
}
